import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePageSettled } from "../../lib/deferred";

const SOCIAL_POPUP_COOKIE = "dar_zaytoun_social_popup_seen";
const ONE_DAY_SECONDS = 24 * 60 * 60;

function hasSeenPopup() {
  return document.cookie.split("; ").some((cookie) => cookie.startsWith(`${SOCIAL_POPUP_COOKIE}=`));
}

function rememberPopup() {
  document.cookie = `${SOCIAL_POPUP_COOKIE}=1; max-age=${ONE_DAY_SECONDS}; path=/; SameSite=Lax`;
}

// Shows a small social invitation once every 24 hours instead of fixed buttons.
export default function SocialFloatingActions() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const contactPhone = import.meta.env.VITE_CONTACT_PHONE_WHATSAPP || "+212770780837";
  const instagramUrl = import.meta.env.VITE_SOCIAL_INSTAGRAM || "https://www.instagram.com/Dar.Zaytoun.Bhalil";
  const whatsappUrl =
    import.meta.env.VITE_SOCIAL_WHATSAPP ||
    `https://wa.me/${contactPhone.replace(/\D/g, "")}`;

  // Wait until the page has settled so the popup doesn't cover the hero (and
  // compete for the main thread) while the page is still loading.
  const settled = usePageSettled();

  useEffect(() => {
    if (!settled || hasSeenPopup()) return;

    const timer = window.setTimeout(() => setIsOpen(true), 1200);
    return () => window.clearTimeout(timer);
  }, [settled]);

  const closePopup = () => {
    rememberPopup();
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-x-3 bottom-4 z-[60] sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[360px]">
      <div className="relative rounded-3xl border border-white/70 bg-[#fffaf3]/95 p-5 shadow-2xl shadow-[#4a2d18]/20 backdrop-blur-xl dark:border-white/10 dark:bg-[#2d1810]/95">
        <button
          type="button"
          onClick={closePopup}
          aria-label={t("socialPopup.close")}
          title={t("socialPopup.close")}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-[#c9b59c] bg-[#f4e7d5] text-[#4a2d18] shadow-sm transition hover:bg-[#eadfd1] hover:text-[#2d1810] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caramel-500 dark:border-white/20 dark:bg-white/10 dark:text-caramel-100 dark:hover:bg-white/20"
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>

        <div className="pr-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-caramel-600 dark:text-caramel-400">
            {t("socialPopup.eyebrow")}
          </p>
          <h2 className="mt-1 font-serif text-xl font-bold text-[#4a2d18] dark:text-caramel-100">
            {t("socialPopup.title")}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-[#73583b] dark:text-caramel-200/75">
            {t("socialPopup.description")}
          </p>
        </div>

        <div className="mt-4 flex gap-2">
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closePopup}
            className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] px-3 py-2.5 text-xs font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#dd2a7b]/30"
          >
            <img src="/icons/brands/instagram-white.svg" alt="" aria-hidden="true" className="h-4 w-4" />
            {t("socialPopup.instagramAction")}
          </a>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closePopup}
            className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl bg-[#075E54] px-3 py-2.5 text-xs font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#064941] hover:shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/30"
          >
            <img src="/icons/brands/whatsapp-white.svg" alt="" aria-hidden="true" className="h-4 w-4" />
            {t("socialPopup.whatsappAction")}
          </a>
        </div>

        <button
          type="button"
          onClick={closePopup}
          className="mt-3 inline-flex w-full items-center justify-center rounded-xl border border-[#c9b59c] bg-[#f5eadc] px-3 py-2.5 text-sm font-bold text-[#4a2d18] shadow-sm transition hover:border-[#a98b69] hover:bg-[#eadfd1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caramel-500 dark:border-white/20 dark:bg-white/10 dark:text-caramel-100 dark:hover:bg-white/20"
        >
          {t("socialPopup.dismiss")}
        </button>
      </div>
    </div>
  );
}
